import { Link, useNavigate } from "react-router-dom";
import { Stethoscope, MapPin, Clock, Zap, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSavedJobs } from "@/hooks/useSavedJobs";
import { toast } from "sonner";
import VerifiedBadge from "@/components/VerifiedBadge";
import HireBadge from "@/components/HireBadge";

const TYPE_COLORS = {
  "Full-time": "bg-[#1E3A2F] text-[#F7F4EB]",
  "Part-time": "bg-[#3B5A6B] text-white",
  "Per Diem": "bg-[#DDA74A] text-[#1E3A2F]",
  "Contract": "bg-[#C05A3D] text-white",
};

export default function JobCard({ job }) {
  const chip = TYPE_COLORS[job.employment_type] || "bg-[#1E3A2F] text-[#F7F4EB]";
  const { user } = useAuth();
  const { isSaved, toggle } = useSavedJobs();
  const nav = useNavigate();
  const saved = isSaved(job.id);

  const onToggleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { nav("/login"); return; }
    const r = await toggle(job.id);
    if (r?.saved) toast.success("Saved to your list");
    else if (r?.saved === false) toast.success("Removed from saved");
  };

  return (
    <Link
      to={`/jobs/${job.id}`}
      data-testid={`job-card-${job.id}`}
      className="abm-card p-5 flex flex-col fade-in relative overflow-hidden"
    >
      <div className="absolute top-4 right-4 flex items-center gap-2">
        {job.start_asap && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full bg-[#C05A3D] text-white">
            <Zap className="w-3 h-3" /> Immediate
          </span>
        )}
        <button
          onClick={onToggleSave}
          data-testid={`job-save-${job.id}`}
          aria-label={saved ? "Unsave job" : "Save job"}
          className={`w-9 h-9 rounded-full grid place-items-center border transition ${saved ? "bg-[#C05A3D] border-[#C05A3D] text-white" : "bg-white border-[#E2DED0] text-[#1E3A2F] hover:border-[#C05A3D]"}`}
        >
          <Heart className={`w-4 h-4 ${saved ? "fill-current" : ""}`} />
        </button>
      </div>
      <div className="flex items-center gap-2 text-[#1E3A2F]">
        <div className="w-10 h-10 rounded-xl bg-[#1E3A2F]/10 grid place-items-center">
          <Stethoscope className="w-5 h-5 text-[#1E3A2F]" />
        </div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#5C5C5C]">{job.role_category}</div>
      </div>
      <h3 className="font-display text-xl text-[#1A1A1A] mt-3 leading-tight line-clamp-2 pr-16">{job.title}</h3>
      <div className="mt-1 text-sm text-[#5C5C5C] flex items-center gap-2 flex-wrap pr-16">
        <span>{job.employer}</span>
        {job.owner_verified && <VerifiedBadge tier={job.owner_verification_tier} size="sm" />}
        {job.owner_hires_count > 0 && <HireBadge count={job.owner_hires_count} />}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
        <span className={`px-2.5 py-1 rounded-full ${chip}`}>{job.employment_type}</span>
        {job.shift && job.shift !== "Any" && (
          <span className="px-2.5 py-1 rounded-full bg-[#F7F4EB] border border-[#E2DED0] text-[#1E3A2F] inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {job.shift}
          </span>
        )}
        {job.pay_range && (
          <span className="px-2.5 py-1 rounded-full bg-[#DDA74A]/15 text-[#8a5b1a] border border-[#DDA74A]/40 font-medium">
            {job.pay_range}
          </span>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-[#E2DED0] flex items-center justify-between text-xs text-[#5C5C5C]">
        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {job.location}</span>
        <span className="text-[#C05A3D] font-medium">View & apply →</span>
      </div>
    </Link>
  );
}
